/**
 * This is a plugin that adds a globally available $tm(), $ti, $td methods to the app.
 *
 * These are used to handle the translation of strings, dates and interpolation of strings.
 *
 * It also supports commonmark markdown in the translations.@
 * @see https://marked.js.org/
 *
 */
import { type DateType, DateFormats, isValidDate } from '@/utilities/dateutils';
import { marked } from 'marked';
import type { App } from 'vue';
import { parseDateStringISO } from '@/utilities/dateutils';
import i18next from 'i18next';

let _app: App<Element>;

/**
 * This is a plugin that adds a globally available $tm() method to the app.
 * This is an important step to make typescript compiler understand the new method.
 */
declare module 'vue' {
    interface ComponentCustomProperties {
        /**
         * Translates a key to the current language. commonmark markdown is supported.
         * @param key
         * @param val
         * @param markdown
         * @returns
         */
        $tm: (key: string, val?: string | number, markdown?: boolean) => string;

        /**
         * Translate a date to the current language. with the current format
         * @param value
         * @param format can be a Date() or a valid ISO string
         * @returns
         */
        $td: (value: Date | string | null, format: DateType) => string;
    }
}

/**
 * This is a class that provides a $tm method to be used in the app.
 * This class should not be used directly, instead use the $tm method.
 */
class i18n {
    /**
     * This will parse a translation key and return the key and the content.
     * the key is only considered valid if it has the format "namespace.key" in lowercase, english alphabet without spaces, and only one dot, and is at least 3 characters long.
     * @param key
     * @returns
     */
    private static parseTranslationKey(key: string): { key: string; content: string } | null {
        const keyRegex = /^[a-z0-9_-]{3,}\.[a-z0-9_-]{2,}$/;
        let content: string = '';
        if (key === null || key === undefined) {
            return null;
        }

        // Split key on first space and return both elements
        if (key?.includes(' ')) {
            const splitKey = key.split(' ');
            key = splitKey[0];

            const contentParts = splitKey.slice(1);
            content = contentParts.join(' ');

            // If the content is less than 20 characters, we assume it is not a valid content
            if (content.length < 20) {
                return null;
            }
        }

        // If the key does not contain one dot, we assume it is text and return it.
        if (key.split('.').length != 2) {
            return null;
        }

        // Check if the key is only contains numbers and a dot
        if (/^\d+\.\d+$/.test(key)) {
            return null;
        }

        // Double check that the key has the correct format we
        // we assume it should have the format "namespace.key" in lowercase, english alphabet without spaces.
        if (!keyRegex.test(key.toLowerCase())) {
            return null;
        }

        return {
            key: key,
            content: content,
        };
    }

    /**
     * Translates a key to the current language. commonmark markdown is supported.
     * @param key
     * @param defaultValue
     * @param markdown
     * @returns
     */
    public static tm(key: string, defaultValue: string | number = '', markdown: boolean = true): string {
        let translated: string;

        const parsedKey = i18n.parseTranslationKey(key);

        if (parsedKey === null) {
            return key;
        }

        // If the default value is a number, we assume it is a count and pass it to i18next to translate plurals
        // TODO: Here we might need more formatting options later. for now we just handle the count.
        //
        // HACK: This is workaround. We use here the exposded $t method instead of implementing the entire logic
        // ourselves. This is because the $t() and t() methods in i18next-vue is not the same method.
        // This will always return a reactive, even tho maybe we wont need it??
        // but this means that we will deffinitely not be able to override the $t method, and therefore we use $tm instead.
        if (!isNaN(parseInt(defaultValue + ''))) {
            translated = _app.config.globalProperties.$t(parsedKey.key, { count: parseInt(defaultValue.toString()) }) as string;
        } else {
            translated = _app.config.globalProperties.$t(parsedKey.key) as string;
        }

        if (markdown) {
            translated = marked.parse(translated).toString();

            // Replace the ![fa-*] with the correct fontawesome icon
            const faRegex = /!\[fa-([a-z0-9-]+)\]/g;
            const faReplace = ''; //'<img src="/src/assets/img/fontawesome/$1-sharp-thin.svg" class="fa-svg-icon">';
            translated = translated.replace(faRegex, faReplace);
        }

        let returnvalue =
            (markdown ? marked.parse(translated).toString() : translated) + (parsedKey.content.length > 0 ? ' ' + parsedKey.content : '');

        // We remove the starting p and ending p tags if markdown is enabled
        if (markdown) {
            returnvalue = returnvalue
                .replace(/^<p>/, '')
                .replace(/<\/p>\n$/, '')
                .replace(/\*/g, '');
        }

        return returnvalue;
    }

    /**
     * Localize a date
     * @param value
     * @param format
     * @returns
     */
    public static td(value: Date | string | null, format: DateType): string {
        if (value === null) return '';

        if (typeof value === 'string') {
            value = parseDateStringISO(value);
        }

        if (!isValidDate(value)) return '';

        const dateString = _app.config.globalProperties.$t('{{val, datetime}}', {
            val: value,
            formatParams: {
                val: DateFormats[format],
            },
            interpolation: { escapeValue: false },
        });

        // Remove the comma between the date and time from the date string.
        return dateString.replace(/, /g, ' ');
    }

    /**
     * Handling interpolation of strings
     * @param key
     * @param values
     * @param markdown
     * @returns
     */
    public static ti(key: string, values: any, markdown: boolean = false): string {
        const parsedKey = i18n.parseTranslationKey(key);

        if (parsedKey === null) {
            if (markdown) {
                // We check if there is any mrkdwn syntax in the key, if so we parse it.
                const markdownRegex = /\*\*(.*?)\*\*|__(.*?)__|`(.*?)`/g;
                const markdownMatches = key.match(markdownRegex);
                if (markdownMatches) {
                    return marked.parse(key).toString();
                }
            }

            return key;
        }

        const txt = _app.config.globalProperties.$t(parsedKey.key, values).toString();

        if (markdown) {
            return marked
                .parse(txt)
                .toString()
                .replace(/^<p>/, '')
                .replace(/<\/p>\n$/, '')
                .replace(/\*/g, '');
        }

        return txt;
    }
}

// Export the $text method for use in the app.
export const $tm = i18n.tm;
export const $td = i18n.td;
export const $ti = i18n.ti;

/**
 * This will build a translation key from a prefix and a value.
 * @param prefix
 * @param value
 * @returns
 */
export const createTranslationKey = (prefix: string, value: string | null): string => {
    if (value === null || value === undefined) {
        return '';
    }

    value = value.trim().toLocaleLowerCase();
    prefix = prefix.trim().toLocaleLowerCase();
    // We need to make sure the value only contains this characters [a-z0-9_-] and is at least 3 characters long. All other characters are removed.
    const regex = /[^a-z0-9_.-]/g;
    const sanitizedValue = value.replace(regex, '');
    const sanitizedPrefix = prefix.replace(regex, '');

    return sanitizedPrefix + '.' + sanitizedValue;
};

/**
 * This is a plugin that adds a globally available $tm() method to the app.
 */
export default {
    install: (app: App<Element>) => {
        _app = app;
        app.config.globalProperties.$tm = (key: string, val: string | number = '', markdown: boolean = false): string => {
            return i18n.tm(key, val, markdown);
        };

        app.config.globalProperties.$td = (value: Date | string | null, format: DateType): string => {
            return i18n.td(value, format);
        };

        /**
         * Handling interpolation of strings
         * @param key
         * @param values
         * @param markdown
         * @returns
         */
        app.config.globalProperties.$ti = (key: string, values: any, markdown: boolean = false): string => {
            return i18n.ti(key, values, markdown);
        };
    },
};
